<template>
  <div class="slideshow" :data-theme="getRouteName === 'home' ? 'red' : false" data-cont="small">
    <transition @enter="enter" @leave="leave" mode="in-out">
      <div class="slideshow__multiple-item" v-if="!isMobile" :key="currentIndex" ref="multipleItem">
        <BasePicture
          class="slideshow__item"
          v-for="(picture) in currentPicture"
          :key="picture.title"
          :data="Object.assign(picture, { tagImg: false, rotation: getRandom(-5, 5) })"
          :lazy="lazy"
          :lottieBg="'speakers'"
          :lottieRef="'slideshow'"
        />
      </div>
    </transition>
    <transition @enter="mobileEnter" @leave="mobileLeave" mode="out-in">
      <BasePicture
        class="slideshow__item"
        :data="Object.assign(currentPicture, { tagImg: false })"
        :lazy="lazy"
        :key="currentIndex"
        v-if="isMobile"
      />
    </transition>
    <button
      class="slideshow__button"
      @click="clickRandom()"
      aria-label="Random"
    >
      <svg version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" x="0px" y="0px" viewBox="0 0 55 55" style="enable-background:new 0 0 55 55;" xml:space="preserve">
        <circle fill="#FC3A31" cx="27.5" cy="27.5" r="25"/>
        <path class="timer__filler" fill="none" stroke="#444444" stroke-width="2" d="M27.5,2.5c13.8,0,25,11.2,25,25s-11.2,25-25,25s-25-11.2-25-25S13.7,2.5,27.5,2.5z" style="transform-origin: 50% 50%" />
      </svg>
      <svg class="slideshow__button-icon">
        <use xlink:href="#reload" />
      </svg>
    </button>
    <div class="ui-container--button">
      <BaseButtonFilled :data="Object.assign(data, { routerLink: data.url.startsWith('/') })" />
    </div>
  </div>
</template>

<script>
import anime from 'animejs';
import lottie from 'lottie-web';
import { mapGetters } from 'vuex';
import BasePicture from '@/components/BasePicture';
import BaseButtonFilled from '@/components/BaseButtonFilled';

import { isMobile, getRandom, qsa, qs } from '@/scripts/utils';
import loop from '@/scripts/loop';
import wave from '@/scripts/animations/wave';

import * as Hammer from 'hammerjs';


export default {
  name: 'slideshow',

  components: {
    BasePicture,
    BaseButtonFilled,
  },

  props: {
    data: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      speakersList: [],
      mobileArray: [],
      currentArray: [],
      currentIndex: 0,

      handleResize: null,

      isMobile: isMobile(),

      lazy: true,

      intervalId: 0,
      currentFillerAnimation: false,
      isIntervalRunning: false,
      swipeDirection: 'left',

      hm: null,
      imagesLoaded: false,
      isAnimating: false,

      getRandom,
    };
  },

  computed: {
    ...mapGetters(['getRouteName', 'getCity']),

    currentPicture() {
      this.currentArray = this.isMobile ? this.mobileArray : this.pictures();
      const currentPicture = this.currentArray[this.currentIndex];
      this.currentPictureLog = currentPicture;

      return currentPicture;
    },
  },

  created() {
    this.$eventHub.$on('windowResize', this.resize);
    this.mobileArray = [].concat.apply([], this.pictures());
  },

  mounted() {
    loop.add(this.checkIn, 'checkIn');
    this.hm = new Hammer(this.$el);
    this.bindHammer();
    this.preloadImages();
    this.$nextTick(() => {
      this.setHeight();
    });
    window.addEventListener('blur', this.stopInterval);
    window.addEventListener('focus', this.startInterval);
  },

  methods: {
    bindHammer() {
      if (this.isMobile) {
        this.hm.on('swipeleft', () => {
          this.swipeDirection = 'left';
          this.clickRandom();
        });
        this.hm.on('swiperight', () => {
          this.swipeDirection = 'right';
          this.clickRandom();
        });
      } else {
        this.hm.off('swipeleft');
        this.hm.off('swiperight');
      }
    },

    setHeight() {
      if (!this.isMobile) {
        this.$el.style.height = `${this.$refs.multipleItem.getBoundingClientRect().height}px`;
      }
    },

    preloadImages() {
      const promises = [];
      this.pictures().flat().forEach((image) => {
        promises.push(new Promise((resolve) => {
          const i = new Image();
          i.src = image.pictureUrl;
          i.addEventListener('load', resolve);
        }));
      });
      this.imagesLoaded = true;
    },

    clickRandom() {
      if (!this.imagesLoaded) return;
      if (this.isAnimating) return;
      this.lazy = false;
      this.setIndex();
      this.stopInterval();
      this.startInterval();
    },

    setIndex() {
      this.currentArray = isMobile() ? this.mobileArray : this.pictures();

      const randomNumber = getRandom(0, (this.currentArray.length - 1));

      this.currentIndex = (randomNumber === this.currentIndex && this.currentArray.length - 1 !== 0 &&
        (randomNumber === 0 || randomNumber === (this.currentArray.length - 1))) ? 1 : randomNumber;
    },

    resize() {
      this.isMobile = isMobile();
      this.bindHammer();
      this.setHeight();
    },

    pictures() {
      const speakersList = [];
      const nSpeaker = this.data.speakers.length;
      let iterations = parseInt(nSpeaker / 3, 10);

      if (nSpeaker % 3 > 0) iterations += 1;
      for (let i = 0; i < iterations; i += 1) {
        speakersList[i] = [];
      }

      const speakers = [];
      this.data.speakers.forEach((id) => {
        speakers.push(this.$store.state.speakers[this.getCity][id]);
      });

      let totalIndex = 0;
      speakersList.forEach((block) => {
        for (let k = 0; k < 3; k += 1) {
          block.push(speakers[totalIndex] ? speakers[totalIndex] : { });
          totalIndex += 1;
        }
      });

      return speakersList;
    },

    enter(el, done) {
      done();
      const bg = qsa('.picture__bg-animation', el);
      const image = qsa('.picture__image-bg', el);
      const titles = qsa('figcaption', el);

      this.bg = bg;

      this.bg.forEach((bg) => {
        bg.style.opacity = 0;
        if (!this.lazy) {
          const anim = this.setNewBg(bg);
        }
      });

      image.forEach((img, index) => {
        anime({
          targets: img,
          duration: 300,
          scale: [1, 0.8],
          rotateZ: [0, this.getRandom(-5, 5)],
          opacity: [0, 1],
          delay: 400 + (index * 200),
          easing: 'easeInOutSine',
          complete: () => {
            if (index === image.length - 1) {
              setTimeout(() => {
                this.isAnimating = false;
              }, 400);
            }
          },
        });
      });
      anime({
        targets: titles,
        duration: 300,
        opacity: [0, 1],
        delay: (e, i) => 400 + (i * 200),
        easing: 'easeInOutSine',
      });
    },

    leave(el, done) {
      this.isAnimating = true;
      // const els = qsa('.picture__container--div', el);
      const titles = qsa('figcaption', el);
      const image = qsa('.picture__image-bg', el);
      // const ease = [0, 1, 0.60, 1];

      anime({
        targets: image,
        duration: 300,
        scale: [0.8, 0.6],
        opacity: [1, 0],
        rotateZ: 0,
        delay: (e, i) => i * 200,
        easing: 'easeInOutSine',
        complete: () => { setTimeout(done, 1600); },
      });
      anime({
        targets: titles,
        duration: 300,
        opacity: [1, 0],
        delay: (e, i) => i * 200,
        easing: 'easeInOutSine',
      });

      const lotties = lottie.getRegisteredAnimations();
      const slideshow = lotties.filter(elem => elem.name === 'slideshow');

      slideshow.forEach((slide, index) => {
        if (index < 3) {
          slide.playSegments([[26, 50], [0, 25]], true);
          setTimeout(() => {
            this.bg[index].style.opacity = 1;
            slide.destroy();
          }, 1600);
        }
      });
    },

    mobileEnter(el, done) {
      this.lazy = false;
      const picture = qs('.picture__container', el);
      const bgs = qsa('.picture__bg-animation', el);
      const offset = picture.getBoundingClientRect().width;
      const title = qs('figacaption', el);

      bgs.forEach((bg) => {
        this.setNewBg(bg);
      });

      anime({
        targets: picture,
        opacity: [0, 1],
        translateX: [this.swipeDirection === 'left' ? (offset + 40) : -(offset + 40), 0],
        rotateZ: [this.swipeDirection === 'left' ? '5deg' : '-5deg', '0deg'],
        duration: 600,
        easing: [0.25, 0.87, 0.43, 1],
        complete: () => { this.isAnimating = false; done(); },
      });
      anime({
        targets: title,
        opacity: [0, 1],
        duration: 600,
        easing: [0.25, 0.87, 0.43, 1],
      });
    },

    mobileLeave(el, done) {
      this.isAnimating = true;
      const picture = qs('.picture__container', el);
      const offset = picture.getBoundingClientRect().width;
      const title = qs('figcaption', el);

      anime({
        targets: picture,
        opacity: [1, 0],
        translateX: [0, this.swipeDirection === 'left' ? -(offset + 40) : (offset + 40)],
        rotateZ: ['0deg', this.swipeDirection === 'left' ? '5deg' : '-5deg'],
        duration: 600,
        easing: [0.25, 0.87, 0.43, 1],
        complete: done,
      });
      anime({
        targets: title,
        opacity: [1, 0],
        duration: 600,
        easing: [0.25, 0.87, 0.43, 1],
      });
    },

    intervalCallback(regenerate = true) {
      if (regenerate) this.clickRandom();
      const filler = qs('.timer__filler', this.$el);
      this.currentFillerAnimation = anime({
        targets: filler,
        duration: 4400,
        strokeDasharray: [filler.getTotalLength(), filler.getTotalLength()],
        strokeDashoffset: [filler.getTotalLength(), 0],
        easing: 'easeInOutSine',
        complete: () => {
          anime({
            targets: filler,
            duration: 600,
            scaleX: {
              value: -1,
              duration: 0,
            },
            strokeDashoffset: filler.getTotalLength(),
            easing: 'easeInOutSine',
            complete: () => {
              anime({
                targets: filler,
                scaleX: 1,
                duration: 0,
              });
            },
          });
        },
      });
    },

    startInterval() {
      if (!this.isIntervalRunning) {
        this.isIntervalRunning = true;
        this.intervalCallback(false);
        this.intervalId = window.setInterval(this.intervalCallback, 5000);
      }
    },

    stopInterval() {
      if (this.isIntervalRunning) {
        this.isIntervalRunning = false;
        window.clearInterval(this.intervalId);
        if (this.currentFillerAnimation) this.currentFillerAnimation.pause();
      }
    },

    checkIn() {
      const elTop = this.$el.getBoundingClientRect().top;
      if (elTop < window.innerHeight && elTop > 0) {
        this.startInterval();
      } else {
        this.stopInterval();
      }
    },

    setNewBg(bg) {
      const newWave = wave(bg, 'speakers');
      newWave.goToAndStop(24, true);

      return newWave;
    },
  },

  beforeDestroy() {
    this.$eventHub.$off('windowResize');
    delete this.currentFillerAnimation;
    loop.remove('checkIn');
    this.hm.off('swipeleft');

    const lotties = lottie.getRegisteredAnimations();
    const slideshow = lotties.filter(elem => elem.name === 'slideshow');
    slideshow.forEach((slide) => { slide.destroy(); });
  },
};
</script>

<style lang="scss" scoped>
@import "../styles/utils";

.slideshow {
  position: relative;
  width: 100%;
  margin-top: 80px;

  &__button {
    position: absolute;
    top: -80px;
    right: 0;
    width: 40px;
    height: 40px;

    border: none;
    background-color: transparent;

    @include mq(mobileL) {
      top: -180px;
      width: 55px;
      height: 55px;
    }

    cursor: pointer;
    outline: transparent;

    svg {
      position: absolute;
      top: 0px;
      left: 0px;
      width: 100%;
      height: 100%;

      &:nth-child(2) {
        width: 22px;
        height: 22px;
        left: 9px;
        top: 8px;
        transform-origin: 50% 50%;
        transition: transform 0.4s $global-ease;

        &:hover {
          transform: scale(0.9);
        }

        @include mq(mobileL) {
          width: 30px;
          height: 30px;
          left: 12px;
          top: 12px;
        }
      }
    }

  }

  &__multiple-item {
    position: absolute;
    width: 100%;

    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    flex-wrap: wrap;

    .slideshow__item {
      flex: 0 0 25%;
      width: 25%;

      &:nth-child(even) {
        flex: 0 0 35%;
        width: 35%;
        margin: 120px 3% 0 0;
      }

      &:nth-child(1) {
        margin-top: 75px;
      }
    }
  }

  &__item {
    position: relative;
    width: 100%;

    @include aspect-ratio(4, 5);
  }

  .ui-container--button {
    position: relative;
    padding-top: 40px;
    justify-content: flex-start;

    @include mq(mobileL) {
      position: absolute;
      bottom: 0;
      right: 0;
      padding-top: 80px;
      
      justify-content: flex-end;
    }

    display: flex;
    align-items: flex-start;
  }
}
</style>
